import React, { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { TypeFilter } from '../common/TypeSelector';
import { Pagination } from '../common/Pagination';
import { SortableHeader } from '../common/SortableHeader';
import { ListEmpty, ListErrorCard, ListLoadingCard } from '../common/ListStateCard';
import { ConfigTemplateDetailModal } from './ConfigTemplateDetailModal';
import { ANEP_CLIENT_ID, ConfigTemplateListItem, isAnepTemplate } from '../../types/configTemplate';
import { useTemplatesList } from './templatesList/useTemplatesList';

interface ConfigTemplatesTableProps {
  onTemplateSelect?: (template: ConfigTemplateListItem) => void;
  refreshTrigger?: number;
  canEdit?: boolean;
  canAssign?: boolean;
}

/**
 * The templates list: a toolbar with the search, the filters and the fleet
 * figures; one row per template (owner, type, default flag, devices); the
 * detail dialog on click. The data lives in `useTemplatesList`.
 */
export const ConfigTemplatesTable: React.FC<ConfigTemplatesTableProps> = ({ onTemplateSelect, refreshTrigger, canEdit = false, canAssign = false }) => {
  const { formatDate, formatNumber, t } = useI18n();
  const list = useTemplatesList(refreshTrigger);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const openTemplate = (template: ConfigTemplateListItem) => {
    setSelectedTemplateId(template.id);
    onTemplateSelect?.(template);
  };

  const closeTemplate = () => setSelectedTemplateId(null);

  const sortHeader = (field: Parameters<typeof list.sortBy>[0], label: string, numeric = false) => (
    <SortableHeader field={field} label={label} activeField={list.sort.field} direction={list.sort.direction} onSort={list.sortBy} numeric={numeric} />
  );

  // Only the first load blanks the card; later refetches keep the toolbar mounted.
  if (list.loading && list.templates.length === 0 && !list.error) return <ListLoadingCard message={t('configTemplates.loading')} />;
  if (list.error) return <ListErrorCard title={t('configTemplates.errorLoading')} message={list.error} onRetry={list.reload} />;

  const { facts } = list;
  const fact = (value: number | null) => (value == null ? '…' : formatNumber(value));

  return (
    <div className="templates-container tpl-container">
      <div className="tpl-card">
        <div className="tpl-toolbar">
          <input
            type="search"
            placeholder={t('configTemplates.searchPlaceholder')}
            value={list.searchTerm}
            onChange={(e) => list.setSearchTerm(e.target.value)}
            className="tpl-search"
            data-testid="field-templates-search"
            aria-label={t('configTemplates.searchPlaceholder')}
          />
          {/* A client account only ever sees its own templates: nothing to filter by. */}
          {list.isAnepStaff && (
            <select
              id="clientFilter"
              data-testid="filter-template-client"
              value={list.clientFilter}
              onChange={(e) => { list.setClientFilter(e.target.value); list.setCurrentPage(1); }}
              className="tpl-select"
              aria-label={t('clients.client')}
            >
              <option value="">{t('configTemplates.filterByClient')}</option>
              <option value={ANEP_CLIENT_ID}>{t('configTemplates.anep')}</option>
              {list.clientList
                .filter((c) => c.active !== false)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <TypeFilter
            value={list.deviceTypeFilter}
            onChange={(value: string) => { list.setDeviceTypeFilter(value); list.setCurrentPage(1); }}
            allLabel={t('deviceTypes.allHardwareTypes')}
            className="tpl-select"
            includeInactive={list.showInactive && list.canSeeInactive}
            ariaLabel={t('deviceTypes.hardwareType')}
          />
          {list.canSeeInactive && (
            <label className="tpl-check" title={t('common.showInactiveHint')}>
              <input type="checkbox" checked={list.showInactive} onChange={(e) => { list.setShowInactive(e.target.checked); list.setCurrentPage(1); }} />
              {t('common.showInactive')}
            </label>
          )}
          {list.hasFilters && (
            <button type="button" className="tpl-link" onClick={list.clearFilters}>{t('configTemplates.clearFilters')}</button>
          )}
          <div className="tpl-facts" data-testid="templates-facts">
            <span><strong>{fact(facts.total)}</strong> {t('configTemplates.facts.templates')}</span>
            <span className="is-ok"><strong>{fact(facts.active)}</strong> {t('configTemplates.facts.active')}</span>
            {facts.inactive != null && facts.inactive > 0 && <span className="is-warning"><strong>{formatNumber(facts.inactive)}</strong> {t('configTemplates.facts.inactive')}</span>}
            <span><strong>{fact(facts.types)}</strong> {t('configTemplates.facts.types')}</span>
          </div>
        </div>

        <div className="tpl-table-wrap">
          <table className="templates-table tpl-table" data-testid="templates-table">
            <thead>
              <tr>
                {sortHeader('name', t('configTemplates.columns.template'))}
                {sortHeader('client_id', t('configTemplates.ownerLabel'))}
                {sortHeader('device_type', t('configTemplates.columns.type'))}
                {sortHeader('device_count', t('configTemplates.devicesColumn'), true)}
                {sortHeader('updated_at', t('configTemplates.columns.updated'))}
                {list.showInactive && <th>{t('configTemplates.columns.status')}</th>}
                <th className="tpl-col-open"><span className="sr-only">{t('configTemplates.open')}</span></th>
              </tr>
            </thead>
            <tbody>
              {list.sorted.map((template) => {
                const owner = list.ownerName(template.client_id);
                const showDescription = template.description && template.description.trim() !== template.name.trim();
                return (
                  <tr key={template.id} onClick={() => openTemplate(template)} className={`template-row tpl-row${template.is_active ? '' : ' is-off'}`} data-testid={`template-row-${template.id}`}>
                    <td className="tpl-cell-name">
                      <div className="tpl-name-line">
                        <strong className="tpl-name">{template.name}</strong>
                        {template.is_default && (
                          <span className="default-badge" title={template.inherited ? t('configTemplates.inheritedDefaultHint') : t('configTemplates.defaultHint')}>
                            {template.inherited ? t('configTemplates.inheritedDefaultBadge') : t('configTemplates.defaultBadge')}
                          </span>
                        )}
                      </div>
                      {showDescription && <span className="tpl-sub">{template.description}</span>}
                    </td>
                    <td className="tpl-cell-owner">
                      {isAnepTemplate(template.client_id)
                        ? <span className="template-client template-owner-anep tpl-owner-anep">{t('configTemplates.anep')}</span>
                        : (
                          <span className="template-client tpl-owner">
                            {owner ?? template.client_name ?? '–'}
                            {template.inherited && <span className="cl-agency-badge is-group" title={t('configTemplates.inheritedHint')}>{t('configTemplates.inheritedBadge')}</span>}
                          </span>
                        )}
                    </td>
                    <td><span className="type-badge tpl-type">{template.device_type}</span></td>
                    <td className="numeric tpl-cell-count">
                      <span className="template-device-count tpl-count">{formatNumber(template.device_count)}</span>
                    </td>
                    <td className="tpl-muted">{formatDate(template.updated_at)}</td>
                    {list.showInactive && (
                      <td>
                        <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
                          {template.is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                    )}
                    <td className="tpl-col-open"><span className="tpl-chevron" aria-hidden="true">›</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {list.sorted.length === 0 && !list.loading && (
            <ListEmpty title={t('configTemplates.noTemplatesFound')} hint={list.hasFilters ? t('configTemplates.emptyHint') : undefined} testId="templates-empty" />
          )}
        </div>

        <Pagination
          page={list.currentPage}
          totalPages={list.serverTotalPages}
          totalItems={list.serverTotalItems}
          pageSize={list.itemsPerPage}
          itemLabel="templates"
          onPageChange={list.setCurrentPage}
          onPageSizeChange={(size) => { list.setItemsPerPage(size); list.setCurrentPage(1); }}
        />
      </div>

      <ConfigTemplateDetailModal
        templateId={selectedTemplateId}
        isOpen={selectedTemplateId !== null}
        canEdit={canEdit}
        canAssign={canAssign}
        onClose={closeTemplate}
        onTemplateUpdated={() => { closeTemplate(); list.reload(); }}
      />
    </div>
  );
};
